import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { TooltipProps } from '@mui/material/Tooltip';
import { getItemPadding } from '@openx/utils/lib/prepareNestedCategoryOptions.ts';
import type { ReactNode } from 'react';

import { Ellipsis } from '../Ellipsis/Ellipsis.tsx';
import { HighlightedPhrase } from '../HighlightedPhrase/HighlightedPhrase.tsx';

interface AutoCompleteRenderOptionsProps {
  option: string;
  phraseToHighlight?: string;
  dataTest?: string;
  children?: ReactNode;
  childrenLocation?: 'above' | 'below';
  getPaddingLevel?: () => number | string | undefined;
  tooltip?: boolean;
  tooltipMessage?: string;
  hasMultiple?: boolean;
  selected?: boolean;
  tooltipPlacement?: TooltipProps['placement'];
}

export const AutoCompleteRenderOptions = ({
  children,
  option,
  childrenLocation = 'below',
  phraseToHighlight,
  dataTest,
  getPaddingLevel,
  tooltip = false,
  hasMultiple = false,
  selected = false,
  tooltipPlacement,
  tooltipMessage,
}: AutoCompleteRenderOptionsProps) => {
  const render = () => (
    <>
      {childrenLocation === 'above' && children}
      <HighlightedPhrase searchPhrase={phraseToHighlight}>{option}</HighlightedPhrase>
      {childrenLocation === 'below' && children}
    </>
  );

  const Icon = selected ? RemoveIcon : AddIcon;

  if (getPaddingLevel) {
    return (
      <Box
        data-test={dataTest}
        sx={{
          paddingLeft: getItemPadding(getPaddingLevel()),
          display: 'flex',
          justifyContent: 'space-between',
          flex: '1',
          maxWidth: '100%',
        }}
      >
        <Ellipsis width="100%" tooltip={tooltip} tooltipPlacement={tooltipPlacement} tooltipMessage={tooltipMessage}>
          {render()}
        </Ellipsis>
        {hasMultiple && (
          <IconButton size="small">
            <Icon />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flex: '1',
        maxWidth: '100%',
      }}
    >
      <Ellipsis
        width="100%"
        tooltip={tooltip}
        data-test={dataTest}
        tooltipPlacement={tooltipPlacement}
        message={tooltipMessage}
      >
        {render()}
      </Ellipsis>
      {hasMultiple && (
        <IconButton size="small">
          <Icon />
        </IconButton>
      )}
    </Box>
  );
};
